
import React, { useState, useMemo } from 'react';
import { Article } from '../types.ts';

const ArticleDetails: React.FC<{article: Article}> = ({article}) => (
    <div className="bg-gray-100 p-3 mt-2 rounded-md text-xs text-gray-700">
        <div className="grid grid-cols-2 gap-x-4 gap-y-1">
            <span><strong>Elasticità:</strong> {article.details.elasticity}</span>
            <span><strong>Disegno:</strong> {article.details.drawing}</span>
            <span><strong>Ordito:</strong> {article.details.warp}</span>
            <span><strong>Trama:</strong> {article.details.weft}</span>
            <span><strong>Colore:</strong> {article.details.color}</span>
            <span><strong>Finissaggio:</strong> {article.details.finishing}</span>
        </div>
    </div>
);

const CollectionItem: React.FC<{ 
  article: Article, 
  isSelected: boolean, 
  onToggleSelect: (id: string) => void,
  onInspect: (id: string) => void,
  isInspected: boolean,
}> = ({ article, isSelected, onToggleSelect, onInspect, isInspected }) => {
  return (
     <li className={`p-3 border my-2 transition-all duration-200 rounded-lg shadow-sm ${isInspected ? 'bg-sky-50 border-sky-300' : 'bg-white border-gray-200'}`}>
      <div className="flex items-center gap-3 cursor-pointer" onClick={() => onInspect(article.id)}>
        <input 
          type="checkbox"
          checked={isSelected}
          onClick={(e) => e.stopPropagation()}
          onChange={() => onToggleSelect(article.id)}
          className="form-checkbox h-5 w-5 text-red-600 rounded focus:ring-red-500 transition duration-150 ease-in-out"
          aria-label={`Seleziona articolo ${article.name} per la rimozione`}
        />
        <div className="flex-grow">
          <span className="font-medium">{article.name}</span>
          <p className="text-xs text-gray-500">{article.code}</p>
        </div>
        <i className={`fas fa-chevron-down text-gray-400 transition-transform duration-200 ${isInspected ? 'rotate-180' : ''}`}></i>
      </div>
      {isInspected && <ArticleDetails article={article} />}
    </li>
  )
}

const CollectionPanel: React.FC<{
  articles: Article[];
  onRemove: (articles: Article[]) => void;
  isDirty: boolean;
  onSave: () => void;
  onInspect: (id: string) => void;
  inspectedArticleId: string | null;
}> = ({ articles, onRemove, isDirty, onSave, onInspect, inspectedArticleId }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const filteredArticles = useMemo(() => {
    if (!searchTerm) return articles;
    return articles.filter(a => 
      a.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
      a.code.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [articles, searchTerm]);
  
  const handleToggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const handleRemoveClick = () => {
    const articlesToRemove = articles.filter(a => selectedIds.has(a.id));
    onRemove(articlesToRemove);
    setSelectedIds(new Set());
  };

  return (
    <div className="bg-white p-4 rounded-lg shadow-lg flex flex-col h-full">
      <div className="flex justify-between items-center mb-4 border-b pb-2">
        <h2 className="text-xl font-bold text-gray-800">Collezione Corrente</h2>
        <button 
          onClick={onSave}
          disabled={!isDirty}
          className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center gap-2 shadow-md"
        >
          <i className="fas fa-save"></i>
          Salva
        </button>
      </div>
      <div className="relative mb-2">
        <input 
          type="text"
          placeholder="Filtra collezione..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className="w-full p-2 pl-8 border border-gray-300 rounded-md shadow-sm bg-white"
        />
        <i className="fas fa-search absolute left-2.5 top-3 text-gray-400"></i>
      </div>
      <p className="text-sm text-gray-500 mb-2">Totale Articoli: {articles.length}</p>
      <ul className="flex-grow overflow-y-auto pr-1 -mr-2">
        {filteredArticles.length === 0 && (
          <div className="text-center text-gray-500 py-10">
            <i className="fas fa-box-open fa-3x text-gray-300 mb-4"></i>
            <p className="font-semibold">La collezione è vuota.</p>
            <p className="text-sm">Aggiungi articoli dal pannello di sinistra.</p>
          </div>
        )}
        {filteredArticles.map(article => (
          <CollectionItem 
            key={article.id}
            article={article}
            isSelected={selectedIds.has(article.id)}
            onToggleSelect={handleToggleSelect}
            onInspect={onInspect}
            isInspected={article.id === inspectedArticleId}
          />
        ))}
      </ul>
      <button 
        onClick={handleRemoveClick}
        disabled={selectedIds.size === 0}
        className="mt-4 w-full bg-red-600 text-white p-2 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition shadow-md"
      >
        Rimuovi dalla Collezione ({selectedIds.size})
      </button>
    </div>
  );
};

export default CollectionPanel;